import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { financeV2Service, CreateTransactionDto, CreateRecurringTransactionDto } from '../../../services/financeV2Service';
import { clientService } from '../../../services/client-service';
import { caseService } from '../../../services/case-service';
import type { Client } from '../../../types/client';
import { CaseStatus, type Case } from '../../../types/case';
import type { Account, Category } from '../../../types/financeV2.types';
import { NativeSelect } from "@/components/ui/native-select"
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/common/FormSection'

import { getErrorMessage } from '../../../utils/error.utils'
import { useFinanceFeatures } from '@/hooks/useFinanceFeatures'
interface QuickChargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function QuickChargeModal({ isOpen, onClose, onSuccess }: QuickChargeModalProps) {
  // Form state
  const [clientId, setClientId] = useState('');
  const [caseId, setCaseId] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [accountId, setAccountId] = useState('');

  // Installments
  const [hasInstallments, setHasInstallments] = useState(false);
  const [installmentCount, setInstallmentCount] = useState('2');

  // Boleto (placeholder): só aparece com a parte "boleto" ligada no servidor
  const [generateBoleto, setGenerateBoleto] = useState(false);
  const { features } = useFinanceFeatures();

  // Data loading
  const [clients, setClients] = useState<Client[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [defaultCategory, setDefaultCategory] = useState<Category | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(true);

  // Load initial data
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  // Load cases when client is selected
  useEffect(() => {
    if (clientId) {
      loadClientCases(clientId);
    } else {
      setCases([]);
      setCaseId('');
    }
  }, [clientId]);

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      setError(null);

      const [clientsRes, accountsRes, categoriesRes] = await Promise.all([
        clientService.getAllClients({ status: 'ACTIVE' }, { page: 1, limit: 1000, sort: 'fullName:asc' }),
        financeV2Service.accounts.list({ isActive: true, limit: 100 }),
        financeV2Service.categories.list({ type: 'INCOME', isActive: true, limit: 100 })
      ]);

      setClients(clientsRes.clients || []);
      setAccounts(accountsRes.data.accounts || []);

      // Set default account (first active account)
      if (accountsRes.data.accounts && accountsRes.data.accounts.length > 0) {
        setAccountId(accountsRes.data.accounts[0].id);
      }

      // Set default category (look for "Honorários" or first income category)
      const honorariosCategory = categoriesRes.data.categories?.find(c =>
        c.name.toLowerCase().includes('honorário') || c.name.toLowerCase().includes('honorario')
      );
      if (honorariosCategory) {
        setDefaultCategory(honorariosCategory);
      } else if (categoriesRes.data.categories && categoriesRes.data.categories.length > 0) {
        setDefaultCategory(categoriesRes.data.categories[0]);
      }

    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar dados'));
    } finally {
      setLoadingData(false);
    }
  };

  const loadClientCases = async (clientId: string) => {
    try {
      const casesRes = await caseService.getAllCases(
        { clientId, status: [CaseStatus.ACTIVE, CaseStatus.SUSPENDED] },
        { page: 1, limit: 100, sort: 'createdAt:desc' }
      );
      setCases(casesRes.cases || []);
    } catch (err) {
      console.error('Error loading client cases:', err);
    }
  };

  // Helper function to get client display name
  const getClientDisplayName = (client: Client): string => {
    if (client.clientType === 'PERSON') {
      return client.personalData?.fullName ||
             `${client.personalData?.firstName || ''} ${client.personalData?.lastName || ''}`.trim() ||
             'Cliente sem nome';
    } else {
      return client.companyData?.companyName ||
             client.companyData?.tradingName ||
             'Empresa sem nome';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!clientId || !description || !amount || !dueDate || !accountId) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Valor inválido');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build metadata with client and case info
      const metadata: Record<string, unknown> = {
        clientId,
        simplified: true
      };
      if (caseId) {
        metadata.caseId = caseId;
      }
      if (generateBoleto) {
        metadata.generateBoleto = true;
      }

      if (hasInstallments) {
        const count = parseInt(installmentCount);
        if (isNaN(count) || count < 2) {
          setError('Número de parcelas inválido (mínimo 2)');
          return;
        }

        // Create recurring transaction (installments)
        // Total rateado no servidor em centavos (a soma das parcelas é exatamente o total)
        const recurringData: CreateRecurringTransactionDto = {
          description,
          totalAmount: numAmount,
          type: 'INCOME',
          accountId,
          categoryId: defaultCategory?.id,
          clientId,
          caseId: caseId || undefined,
          startDate: dueDate,
          installments: count,
          intervalType: 'MONTHLY',
          notes: notes || undefined,
          metadata
        };

        await financeV2Service.transactions.createRecurring(recurringData);
      } else {
        // Create single transaction
        const transactionData: CreateTransactionDto = {
          description,
          amount: numAmount,
          type: 'INCOME',
          accountId,
          categoryId: defaultCategory?.id,
          clientId,
          caseId: caseId || undefined,
          dueDate,
          notes: notes || undefined,
          metadata
        };

        await financeV2Service.transactions.create(transactionData);
      }

      // Success!
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar cobrança'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setClientId('');
    setCaseId('');
    setDescription('');
    setAmount('');
    setDueDate('');
    setNotes('');
    setHasInstallments(false);
    setInstallmentCount('2');
    setGenerateBoleto(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !loading) handleClose() }}>
      <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Nova Cobrança Rápida</DialogTitle>
          <DialogDescription>Honorário ou cobrança a receber de um cliente, à vista ou parcelada.</DialogDescription>
        </DialogHeader>

        {loadingData ? (
          <div className="flex flex-col gap-4" aria-busy="true">
            <span className="sr-only">Carregando dados...</span>
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : (
          <form id="quick-charge-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
            {error && (
              <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <FormField label="Cliente *" htmlFor="charge-client">
              <NativeSelect
                id="charge-client"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                required
              >
                <option value="">Selecione um cliente</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {getClientDisplayName(client)}
                  </option>
                ))}
              </NativeSelect>
            </FormField>

            <FormField label="Processo (opcional)" htmlFor="charge-case">
              <NativeSelect
                id="charge-case"
                value={caseId}
                onChange={(e) => setCaseId(e.target.value)}
                disabled={!clientId || cases.length === 0}
              >
                <option value="">
                  {!clientId ? 'Selecione um cliente primeiro' : 'Sem vínculo com processo'}
                </option>
                {cases.map((caseItem) => (
                  <option key={caseItem.id} value={caseItem.id}>
                    {caseItem.caseNumber} - {caseItem.title}
                  </option>
                ))}
              </NativeSelect>
            </FormField>

            <FormField label="Descrição *" htmlFor="charge-description">
              <Input
                id="charge-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Honorários advocatícios - Processo trabalhista"
                required
              />
            </FormField>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Valor Total *" htmlFor="charge-amount">
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
                  <Input
                    id="charge-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0,00"
                    className="pl-9"
                    required
                  />
                </div>
              </FormField>

              <FormField label={hasInstallments ? 'Primeiro Vencimento *' : 'Data de Vencimento *'} htmlFor="charge-due-date">
                <Input
                  id="charge-due-date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  required
                />
              </FormField>
            </div>

            <FormField label="Conta *" htmlFor="charge-account">
              <NativeSelect
                id="charge-account"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                required
              >
                <option value="">Selecione uma conta</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.type})
                  </option>
                ))}
              </NativeSelect>
            </FormField>

            <div className="flex flex-col gap-3 rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="hasInstallments"
                  checked={hasInstallments}
                  onChange={(e) => setHasInstallments(e.target.checked)}
                  className="size-4 rounded-sm border-input accent-primary"
                />
                <Label htmlFor="hasInstallments" className="font-normal">Parcelar cobrança</Label>
              </div>

              {hasInstallments && (
                <FormField
                  label="Número de Parcelas"
                  htmlFor="charge-installments"
                  hint={`Valor por parcela (aprox.): ${amount ? `R$ ${(parseFloat(amount) / parseInt(installmentCount || '2')).toFixed(2)}` : 'R$ 0,00'} — os centavos são distribuídos para a soma fechar no total`}
                >
                  <Input
                    id="charge-installments"
                    type="number"
                    min="2"
                    max="120"
                    value={installmentCount}
                    onChange={(e) => setInstallmentCount(e.target.value)}
                  />
                </FormField>
              )}
            </div>

            {/* Boleto (placeholder) — desligado por padrão (feature flag) */}
            {features.boleto && (
              <div className="flex flex-col gap-2 rounded-lg border bg-muted/50 p-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="generateBoleto"
                    checked={generateBoleto}
                    onChange={(e) => setGenerateBoleto(e.target.checked)}
                    className="size-4 rounded-sm border-input accent-primary"
                    disabled
                  />
                  <Label htmlFor="generateBoleto" className="font-normal text-muted-foreground">Emitir Boleto (em breve)</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Funcionalidade de geração de boleto será implementada em breve
                </p>
              </div>
            )}

            <FormField label="Observações" htmlFor="charge-notes">
              <Textarea
                id="charge-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Informações adicionais sobre esta cobrança..."
                className="resize-none"
              />
            </FormField>

            {defaultCategory && (
              <p className="rounded-lg border bg-muted/50 p-3 text-xs text-muted-foreground">
                <strong className="font-medium text-foreground">Categoria padrão:</strong> {defaultCategory.name}
              </p>
            )}
          </form>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="quick-charge-form" loading={loading} disabled={loadingData}>
            {loading ? 'Criando...' : <><Plus className="size-4" /> Criar Cobrança</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
